// Tool-Rechte: Normalisieren, Zusammenführen und Umschalten der Rechte je
// Werkzeug, Gruppe und Person. Aus ToolSettingsPage.jsx herausgelöst
// (23.09.2026), damit die mobile Oberfläche dieselbe Rechnung nutzt.
//
// Wirksam für ein Konto: Admin → alles; sonst Standard, ergänzt um die Rechte
// aktiver Gruppen (combineAccessGrants), dann überschrieben durch die
// persönlichen Rechte (mergeAccessMaps).

export const TOOL_DEFINITIONS = [
  { key: 'offers', label: 'Angebote' },
  { key: 'exposes', label: 'Exposés' },
  { key: 'flyer', label: 'Angebote – Flyer' },
  { key: 'vertrieb', label: 'Angebote – Vertriebspräsentation' },
  { key: 'generator', label: 'Angebote – Generator' },
  { key: 'designsystem', label: 'Einstellungen – Design System' },
  { key: 'vermittlervertrag', label: 'Vermittlervertrag' },
  { key: 'contractTemplates', label: 'Verträge – Vorlagen' },
  { key: 'contractsAm', label: 'Verträge – Einzelvertrag' },
  { key: 'contractsAmQueue', label: 'Verträge – Deals & Bulk' },
  // Wie am Server ohne Alias: zuerst nur Admins, einzeln freischaltbar (21.10.2026).
  { key: 'dokumentenAssistent', label: 'Verträge – Dokumenten-Assistent' },
  { key: 'ffpvTeaser', label: 'Angebote – Projektteaser FFPV' },
  { key: 'contractsAmAutomations', label: 'Einstellungen – Automatisierung' },
  { key: 'contractsAmClauses', label: 'Verträge – Paragraphen' },
  { key: 'standardDocumentTemplates', label: 'Standarddokumente – Vorlagen' },
  { key: 'standardDocuments', label: 'Standarddokumente – Erstellen' },
  { key: 'standardDocumentsFax', label: 'Standarddokumente – Fax' },
  { key: 'onboarding', label: 'On-Boarding – E-Mail-Signatur' },
  { key: 'mittwaldMail', label: 'On-Boarding – Mittwald-Postfach' },
  { key: 'bitrixUser', label: 'On-Boarding – Bitrix-Zugang' },
  // Stand gleich wie in api/_lib/toolPermissionsStore.js. Fehlte er hier,
  // verwarf das Frontend beim Speichern den Eintrag, und der Server leitete
  // ihn jedes Mal neu aus "Angebote" ab - einzeln setzen ging nirgends.
  { key: 'eegRechner', label: 'Werkzeuge – EEG-Rechner' },
];

export const LEGACY_TOOL_ALIASES = {
  exposes: ['offers'],
  generator: ['offers', 'vertrieb'],
  vermittlervertrag: ['contracts'],
  contractTemplates: ['contracts'],
  contractsAm: ['contracts'],
  contractsAmQueue: ['contracts'],
  // Wie am Server: Der Projektteaser gilt anfangs genau wie "Verträge - Deals".
  ffpvTeaser: ['contractsAmQueue', 'contracts'],
  contractsAmAutomations: ['contracts'],
  contractsAmClauses: ['contracts'],
  standardDocumentTemplates: ['contracts'],
  standardDocuments: ['contracts'],
  standardDocumentsFax: ['standardDocuments', 'contracts'],
  onboarding: ['contracts'],
  mittwaldMail: ['onboarding', 'contracts'],
  bitrixUser: ['onboarding', 'contracts'],
  // Wie am Server: Ohne eigenen Eintrag erbt der Rechner das Angebots-Recht.
  // So sieht niemand beim ersten Laden etwas anderes als bisher.
  eegRechner: ['offers'],
};

export const ACCESS_FLAGS = [
  { key: 'canView', label: 'Sehen' },
  { key: 'canCreate', label: 'Erstellen' },
  { key: 'canEdit', label: 'Bearbeiten' },
];

export const DEFAULT_CONTRACT_POLICY = {
  maxContractsPerMonth: 25,
  canEditHtml: true,
  canDownloadGeneratedHtml: true,
  canDownloadGeneratedPdf: true,
  canDownloadOriginalHtmlTemplate: false,
  canDownloadOriginalPdfTemplate: false,
  canEditCommissions: false,
  allowedTemplateVariants: {
    ci: true,
    druckversion: true,
  },
};

export const createEmptyAccessMap = () => Object.fromEntries(
  TOOL_DEFINITIONS.map((tool) => [tool.key, { canView: false, canCreate: false, canEdit: false }])
);

export const normalizeToolAccess = (access = {}) => {
  const canCreate = access?.canCreate === true;
  const canEdit = access?.canEdit === true;
  const canView = access?.canView === true || canCreate || canEdit;

  return { canView, canCreate, canEdit };
};

export const resolveRawToolAccess = (accessMap, toolKey) => {
  if (accessMap?.[toolKey] && typeof accessMap[toolKey] === 'object') {
    return accessMap[toolKey];
  }

  return (LEGACY_TOOL_ALIASES[toolKey] || [])
    .map((legacyKey) => accessMap?.[legacyKey])
    .find((candidate) => candidate && typeof candidate === 'object') || {};
};

export const normalizeAccessMap = (accessMap = {}) => Object.fromEntries(
  TOOL_DEFINITIONS.map((tool) => [tool.key, normalizeToolAccess(resolveRawToolAccess(accessMap, tool.key))])
);

export const normalizeUserAccessMap = (userAccess = {}) => Object.fromEntries(
  Object.entries(userAccess || {})
    .map(([email, access]) => [String(email || '').trim().toLowerCase(), normalizeAccessMap(access)])
    .filter(([email]) => Boolean(email))
);

export const normalizeTextList = (value = []) => {
  if (Array.isArray(value)) {
    return Array.from(new Set(
      value
        .map((entry) => String(entry || '').trim())
        .filter(Boolean)
    )).sort((left, right) => left.localeCompare(right));
  }

  return normalizeTextList(String(value || '').split(/[\n,;]+/g));
};

export const normalizeEmailList = (value = []) => normalizeTextList(value)
  .map((email) => String(email || '').trim().toLowerCase())
  .filter(Boolean);

export const normalizeDealVisibility = (visibility = {}) => {
  const raw = visibility && typeof visibility === 'object' ? visibility : {};

  return {
    mode: raw.mode === 'restricted' ? 'restricted' : 'all',
    bitrixUserIds: normalizeTextList(raw.bitrixUserIds || raw.bitrix_user_ids || raw.responsibleIds || []),
    departmentIds: normalizeTextList(raw.departmentIds || raw.department_ids || raw.structureIds || []),
    includeSubDepartments: raw.includeSubDepartments !== false && raw.include_sub_departments !== false,
  };
};

export const normalizeGroupId = (value, fallback = 'gruppe') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '') || fallback;

export const createEmptyGroup = (name = '') => {
  const label = String(name || '').trim() || 'Neue Gruppe';
  const id = normalizeGroupId(label, `gruppe-${Date.now()}`);

  return {
    id,
    name: label,
    description: '',
    active: true,
    memberEmails: [],
    access: createEmptyAccessMap(),
    contractPolicy: DEFAULT_CONTRACT_POLICY,
    dealVisibility: normalizeDealVisibility(),
  };
};

export const normalizePermissionGroup = (group = {}, index = 0) => ({
  id: normalizeGroupId(group.id || group.key || group.name, `gruppe-${index + 1}`),
  name: String(group.name || `Gruppe ${index + 1}`).trim(),
  description: String(group.description || '').trim(),
  active: group.active !== false,
  memberEmails: normalizeEmailList(group.memberEmails || group.member_emails || group.members || group.emails || []),
  access: normalizeAccessMap(group.access || group.toolAccess || group.tool_access || createEmptyAccessMap()),
  contractPolicy: normalizeContractPolicy(group.contractPolicy || group.contract_policy || DEFAULT_CONTRACT_POLICY),
  dealVisibility: normalizeDealVisibility(group.dealVisibility || group.deal_visibility || {}),
});

export const normalizePermissionGroups = (groups = []) => {
  if (!Array.isArray(groups)) {
    return [];
  }

  const byId = new Map();
  groups.forEach((group, index) => {
    const normalized = normalizePermissionGroup(group, index);
    byId.set(normalized.id, normalized);
  });

  return Array.from(byId.values()).sort((left, right) => left.name.localeCompare(right.name));
};

export const mergeAccessMaps = (baseAccess = {}, overrideAccess = {}) => {
  const normalizedBase = normalizeAccessMap(baseAccess);
  const normalizedOverride = normalizeAccessMap(overrideAccess);

  return Object.fromEntries(
    TOOL_DEFINITIONS.map((tool) => [tool.key, normalizeToolAccess({
      ...normalizedBase[tool.key],
      ...(normalizedOverride[tool.key] || {}),
    })])
  );
};

export const combineAccessGrants = (baseAccess = {}, grantAccess = {}) => {
  const normalizedBase = normalizeAccessMap(baseAccess);
  const normalizedGrants = normalizeAccessMap(grantAccess);

  return Object.fromEntries(
    TOOL_DEFINITIONS.map((tool) => [tool.key, normalizeToolAccess({
      canView: normalizedBase[tool.key].canView || normalizedGrants[tool.key].canView,
      canCreate: normalizedBase[tool.key].canCreate || normalizedGrants[tool.key].canCreate,
      canEdit: normalizedBase[tool.key].canEdit || normalizedGrants[tool.key].canEdit,
    })])
  );
};

export const combineGroupAccess = (groups = []) => {
  const combined = createEmptyAccessMap();

  groups.forEach((group) => {
    const access = normalizeAccessMap(group.access);
    TOOL_DEFINITIONS.forEach((tool) => {
      combined[tool.key] = normalizeToolAccess({
        canView: combined[tool.key].canView || access[tool.key].canView,
        canCreate: combined[tool.key].canCreate || access[tool.key].canCreate,
        canEdit: combined[tool.key].canEdit || access[tool.key].canEdit,
      });
    });
  });

  return combined;
};

export const groupsForEmail = (groups = [], email = '') => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  return groups.filter((group) => group.active && group.memberEmails.includes(normalizedEmail));
};

export const combineGroupContractPolicies = (groups = []) => {
  if (!groups.length) {
    return null;
  }

  return groups.reduce((combined, group) => {
    const policy = normalizeContractPolicy(group.contractPolicy);
    return normalizeContractPolicy({
      maxContractsPerMonth: Math.max(Number(combined.maxContractsPerMonth || 0), Number(policy.maxContractsPerMonth || 0)),
      canEditHtml: combined.canEditHtml || policy.canEditHtml,
      canDownloadGeneratedHtml: combined.canDownloadGeneratedHtml || policy.canDownloadGeneratedHtml,
      canDownloadGeneratedPdf: combined.canDownloadGeneratedPdf || policy.canDownloadGeneratedPdf,
      canDownloadOriginalHtmlTemplate: combined.canDownloadOriginalHtmlTemplate || policy.canDownloadOriginalHtmlTemplate,
      canDownloadOriginalPdfTemplate: combined.canDownloadOriginalPdfTemplate || policy.canDownloadOriginalPdfTemplate,
      canEditCommissions: combined.canEditCommissions || policy.canEditCommissions,
      allowedTemplateVariants: {
        ci: combined.allowedTemplateVariants?.ci || policy.allowedTemplateVariants?.ci,
        druckversion: combined.allowedTemplateVariants?.druckversion || policy.allowedTemplateVariants?.druckversion,
      },
    });
  }, normalizeContractPolicy({ maxContractsPerMonth: 1 }));
};

export const normalizeContractPolicy = (policy = {}) => ({
  maxContractsPerMonth: Math.max(1, Number(policy.maxContractsPerMonth || DEFAULT_CONTRACT_POLICY.maxContractsPerMonth)),
  canEditHtml: policy.canEditHtml !== false,
  canDownloadGeneratedHtml: policy.canDownloadGeneratedHtml !== false,
  canDownloadGeneratedPdf: Object.prototype.hasOwnProperty.call(policy, 'canDownloadGeneratedPdf')
    ? policy.canDownloadGeneratedPdf !== false
    : (Object.prototype.hasOwnProperty.call(policy, 'canDownloadPdfTemplate') ? policy.canDownloadPdfTemplate !== false : true),
  canDownloadOriginalHtmlTemplate: policy.canDownloadOriginalHtmlTemplate === true,
  canDownloadOriginalPdfTemplate: policy.canDownloadOriginalPdfTemplate === true,
  canEditCommissions: policy.canEditCommissions === true,
  allowedTemplateVariants: (() => {
    const variants = policy.allowedTemplateVariants && typeof policy.allowedTemplateVariants === 'object'
      ? policy.allowedTemplateVariants
      : {};
    const normalized = {
      ci: variants.ci !== false,
      druckversion: variants.druckversion !== false,
    };

    if (!normalized.ci && !normalized.druckversion) {
      normalized.ci = true;
    }

    return normalized;
  })(),
});

export const normalizeContractPolicies = (policies = {}) => Object.fromEntries(
  Object.entries(policies || {})
    .map(([email, policy]) => [String(email || '').trim().toLowerCase(), normalizeContractPolicy(policy)])
    .filter(([email]) => Boolean(email))
);

export const withFlagUpdate = (access, flagKey, checked) => {
  const nextAccess = { ...normalizeToolAccess(access), [flagKey]: checked };

  if ((flagKey === 'canCreate' || flagKey === 'canEdit') && checked) {
    nextAccess.canView = true;
  }

  if (flagKey === 'canView' && !checked) {
    nextAccess.canCreate = false;
    nextAccess.canEdit = false;
  }

  return normalizeToolAccess(nextAccess);
};

// Was ein Konto tatsächlich darf, so wie der Server es rechnet
// (api/_lib/toolPermissionsStore.js): Admin → alles; sonst Standard ODER
// aktive Gruppen, darüber die persönlichen Rechte.
export const wirksamerZugriff = ({ istAdmin = false, standard = {}, gruppen = [], persoenlich = null } = {}) => {
  if (istAdmin) {
    return Object.fromEntries(
      TOOL_DEFINITIONS.map((tool) => [tool.key, { canView: true, canCreate: true, canEdit: true }])
    );
  }

  const ausGruppen = combineAccessGrants(standard, combineGroupAccess(gruppen));
  return persoenlich ? mergeAccessMaps(ausGruppen, persoenlich) : ausGruppen;
};

// Vertragslimits eines Kontos: persönliche Regel vor Gruppen vor Standard.
export const wirksameVertragsregel = ({ standard = DEFAULT_CONTRACT_POLICY, gruppen = [], persoenlich = null } = {}) => (
  normalizeContractPolicy(persoenlich || combineGroupContractPolicies(gruppen) || standard)
);

// ---------------------------------------------------------------------------
// Ab hier: Hilfen für die mobile Oberfläche (Bereich "Rechte", 23.09.2026).
// Dieselbe Rechnung wie ToolSettingsPage, nur als reine Funktionen, damit sie
// sich testen lassen und der mobile Speicher sie unverändert benutzen kann.
// ---------------------------------------------------------------------------

const normalisiereMail = (email) => String(email || '').trim().toLowerCase();

// Dieselben Konten wie BUILT_IN_ADMIN_EMAILS in App.js und
// api/_lib/supabaseAuth.js (Test: api/_lib/adminKonten.test.js) - der Server kennt
// zusätzlich CONTRACT_ADMIN_EMAILS, die kommen über isAdmin in der Nutzerliste.
export const EINGEBAUTE_ADMINS = [
  'test@roofshare-solutions.com',
  'r.kirsten@roofshare-solutions.com',
  'r.kirsten@en-power.eu',
  'l.klein@en-power.eu',
];

export const istAdminKonto = (email, nutzerListe = [], eigene = null) => {
  const e = normalisiereMail(email);
  if (!e) return false;
  if (EINGEBAUTE_ADMINS.includes(e)) return true;
  if (eigene && eigene.isAdmin === true && normalisiereMail(eigene.email) === e) return true;
  return (nutzerListe || []).some((n) => normalisiereMail(n?.email) === e && n?.isAdmin === true);
};

export const siehtWieEmailAus = (wert) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(wert || '').trim());

// Die vier Gruppen der Übergabe (Demo, Daten "W"), Verträge zuerst. Die
// Kurznamen stehen unter der Gruppenüberschrift, deshalb ohne "Verträge –".
// Seit 08.10.2026 nach dem Menü benannt und einsortiert (der Projektteaser
// steht im Menü unter Angebote); was jedes Häkchen bewirkt: toolRechteTexte.js.
export const WERKZEUG_GRUPPEN = [
  {
    name: 'Verträge',
    werkzeuge: [
      ['contractsAm', 'Einzelvertrag'], ['contractsAmQueue', 'Deals & Bulk'], ['dokumentenAssistent', 'Dokumenten-Assistent'],
      ['contractTemplates', 'Vorlagen'], ['contractsAmClauses', 'Paragraphen'], ['contractsAmAutomations', 'Automatisierung'],
      ['vermittlervertrag', 'Vermittlervertrag'],
    ],
  },
  {
    name: 'Angebote und Exposés',
    werkzeuge: [
      ['offers', 'Angebote'], ['exposes', 'Exposés'], ['flyer', 'Flyer'], ['ffpvTeaser', 'Projektteaser FFPV'],
      ['vertrieb', 'Vertriebspräsentation'], ['generator', 'Generator'], ['designsystem', 'Design System'],
      ['eegRechner', 'EEG-Rechner'],
    ],
  },
  {
    name: 'Standarddokumente',
    werkzeuge: [['standardDocumentTemplates', 'Vorlagen'], ['standardDocuments', 'Erstellen'], ['standardDocumentsFax', 'Fax']],
  },
  {
    name: 'On-Boarding',
    werkzeuge: [['onboarding', 'E-Mail-Signatur'], ['mittwaldMail', 'Mittwald-Postfach'], ['bitrixUser', 'Bitrix-Zugang']],
  },
];

/**
 * Ordnet jedes Werkzeug genau einer Gruppe zu. Kommt in TOOL_DEFINITIONS ein
 * neues Werkzeug dazu, das hier noch nicht einsortiert ist, landet es unter
 * "Weitere" - es darf am Handy nie einfach fehlen, sonst ließe sich sein Recht
 * dort nicht sehen und nicht ändern.
 */
export const werkzeugGruppen = (definitionen = TOOL_DEFINITIONS) => {
  const bekannt = new Map(definitionen.map((t) => [t.key, t.label]));
  const vergeben = new Set();
  const gruppen = WERKZEUG_GRUPPEN.map((g) => ({
    name: g.name,
    werkzeuge: g.werkzeuge
      .filter(([key]) => bekannt.has(key) && !vergeben.has(key))
      .map(([key, label]) => { vergeben.add(key); return { key, label }; }),
  })).filter((g) => g.werkzeuge.length);
  const rest = definitionen.filter((t) => !vergeben.has(t.key)).map((t) => ({ key: t.key, label: t.label }));
  return rest.length ? [...gruppen, { name: 'Weitere', werkzeuge: rest }] : gruppen;
};

/** "Sehen · Erstellen · Bearbeiten", "Nur sehen" oder "Kein Zugriff". */
export const zugriffZusammenfassung = (zugriff) => {
  const z = normalizeToolAccess(zugriff);
  if (!z.canView) return 'Kein Zugriff';
  if (!z.canCreate && !z.canEdit) return 'Nur sehen';
  return ACCESS_FLAGS.filter((f) => z[f.key]).map((f) => f.label).join(' · ');
};

export const sichtbareBereiche = (zugriffsKarte, schluessel = TOOL_DEFINITIONS.map((t) => t.key)) => {
  const karte = normalizeAccessMap(zugriffsKarte);
  return schluessel.filter((k) => karte[k]?.canView).length;
};

export const alleRechte = (an) => Object.fromEntries(
  TOOL_DEFINITIONS.map((tool) => [tool.key, { canView: an, canCreate: an, canEdit: an }])
);

export const gruppenKurzinfo = (gruppe) => {
  const mitglieder = (gruppe?.memberEmails || []).length;
  const sichtbar = sichtbareBereiche(gruppe?.access);
  return `${mitglieder} ${mitglieder === 1 ? 'Mitglied' : 'Mitglieder'} · ${sichtbar} ${sichtbar === 1 ? 'Bereich' : 'Bereiche'} sichtbar`;
};

// Die acht Schalter der Vertragslimits, Beschriftungen wie in der Übergabe.
// variante: liegt unter allowedTemplateVariants.
export const VERTRAGSREGEL_SCHALTER = [
  { key: 'canEditHtml', label: 'HTML-Bearbeitung' },
  { key: 'canDownloadGeneratedHtml', label: 'Vertrag als HTML' },
  { key: 'canDownloadGeneratedPdf', label: 'Vertrag als PDF' },
  { key: 'canDownloadOriginalHtmlTemplate', label: 'Original-HTML' },
  { key: 'canDownloadOriginalPdfTemplate', label: 'Original-PDF' },
  { key: 'canEditCommissions', label: 'Provisionen anpassen' },
  { key: 'ci', label: 'Normalversion', variante: true },
  { key: 'druckversion', label: 'Druckversion', variante: true },
];

export const regelSchalterWert = (regel, schalter) => (schalter.variante
  ? regel?.allowedTemplateVariants?.[schalter.key] === true
  : regel?.[schalter.key] === true);

/**
 * Änderung für einen Schalter. null heißt: nicht erlaubt - die letzte erlaubte
 * Vorlagenvariante lässt sich nicht abschalten (normalizeContractPolicy würde
 * sonst still die andere wieder einschalten, und der Schalter spränge zurück).
 */
export const regelSchalterPatch = (regel, schalter, an) => {
  if (!schalter.variante) return { [schalter.key]: an };
  const varianten = { ...(regel?.allowedTemplateVariants || {}), [schalter.key]: an };
  if (!varianten.ci && !varianten.druckversion) return null;
  return { allowedTemplateVariants: varianten };
};

// ---------- Entwurf: laden, speichern, zählen ----------

/** Wie refreshSettings in ToolSettingsPage: Serverstand → normalisierter Entwurf. */
export const entwurfAusEinstellungen = (data = {}) => ({
  toolDefault: normalizeAccessMap(data?.toolDefaultAccess || createEmptyAccessMap()),
  toolGroups: normalizePermissionGroups(data?.toolGroups || []),
  toolUser: normalizeUserAccessMap(data?.toolUserAccess || {}),
  contractDefault: normalizeContractPolicy(data?.defaultPolicy),
  contractUser: normalizeContractPolicies(data?.userPolicies),
});

/** Der Körper für POST /contracts/settings - derselbe wie handleSave. */
export const speicherKoerper = (entwurf) => ({
  defaultPolicy: entwurf.contractDefault,
  userPolicies: entwurf.contractUser,
  toolDefaultAccess: entwurf.toolDefault,
  toolGroups: entwurf.toolGroups,
  toolUserAccess: entwurf.toolUser,
});

const gleichWert = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const zaehleZugriff = (alt, neu) => {
  const a = normalizeAccessMap(alt);
  const n = normalizeAccessMap(neu);
  return TOOL_DEFINITIONS.filter((t) => !gleichWert(a[t.key], n[t.key])).length;
};

const zaehleRegel = (alt, neu) => {
  const a = normalizeContractPolicy(alt);
  const n = normalizeContractPolicy(neu);
  const zahl = a.maxContractsPerMonth === n.maxContractsPerMonth ? 0 : 1;
  return zahl + VERTRAGSREGEL_SCHALTER.filter((s) => regelSchalterWert(a, s) !== regelSchalterWert(n, s)).length;
};

// Ein neu angelegter oder entfernter Eintrag zählt als eine Änderung, sonst
// jede geänderte Stelle darin.
const zaehleEintraege = (alt = {}, neu = {}, vergleich) => {
  const schluessel = new Set([...Object.keys(alt || {}), ...Object.keys(neu || {})]);
  let zahl = 0;
  schluessel.forEach((k) => {
    if (!alt?.[k] || !neu?.[k]) zahl += 1;
    else zahl += vergleich(alt[k], neu[k]);
  });
  return zahl;
};

const zaehleGruppe = (alt, neu) => {
  const felder = ['name', 'description', 'active'].filter((f) => alt[f] !== neu[f]).length;
  const altM = new Set(alt.memberEmails || []);
  const neuM = new Set(neu.memberEmails || []);
  const mitglieder = [...altM].filter((m) => !neuM.has(m)).length + [...neuM].filter((m) => !altM.has(m)).length;
  const sichtA = normalizeDealVisibility(alt.dealVisibility);
  const sichtN = normalizeDealVisibility(neu.dealVisibility);
  const sicht = Object.keys(sichtA).filter((k) => !gleichWert(sichtA[k], sichtN[k])).length;
  return felder + mitglieder + sicht + zaehleZugriff(alt.access, neu.access) + zaehleRegel(alt.contractPolicy, neu.contractPolicy);
};

/** Wie viele Stellen der Entwurf vom geladenen Stand abweicht (0 = nichts zu speichern). */
export const zaehleAenderungen = (geladen, entwurf) => {
  if (!geladen || !entwurf) return 0;
  const gruppenAlt = Object.fromEntries((geladen.toolGroups || []).map((g) => [g.id, g]));
  const gruppenNeu = Object.fromEntries((entwurf.toolGroups || []).map((g) => [g.id, g]));
  return zaehleZugriff(geladen.toolDefault, entwurf.toolDefault)
    + zaehleRegel(geladen.contractDefault, entwurf.contractDefault)
    + zaehleEintraege(geladen.toolUser, entwurf.toolUser, zaehleZugriff)
    + zaehleEintraege(geladen.contractUser, entwurf.contractUser, zaehleRegel)
    + zaehleEintraege(gruppenAlt, gruppenNeu, zaehleGruppe);
};

// ---------- Personen ----------

/** Alle Personen für die Liste - zusammengesetzt wie combinedToolUsers. */
export const personenListe = ({ nutzer = [], toolUser = {}, contractUser = {}, gruppen = [] } = {}) => {
  const zeilen = new Map();
  (nutzer || []).forEach((u) => {
    const email = normalisiereMail(u?.email);
    if (email) zeilen.set(email, { ...u, email });
  });
  const ergaenze = (email, source) => { if (!zeilen.has(email)) zeilen.set(email, { id: email, email, source }); };
  Object.keys(toolUser || {}).forEach((email) => ergaenze(email, 'sonderregel'));
  Object.keys(contractUser || {}).forEach((email) => ergaenze(email, 'sonderregel'));
  (gruppen || []).forEach((g) => (g.memberEmails || []).forEach((email) => ergaenze(email, 'gruppe')));
  return Array.from(zeilen.values()).sort((a, b) => a.email.localeCompare(b.email));
};

export const filterePersonen = (liste, suche) => {
  const q = String(suche || '').trim().toLowerCase();
  return q ? liste.filter((p) => p.email.includes(q)) : liste;
};

/** Status einer Person: Admin · Sonderregel · Gruppe: … · Standardrechte. */
export const personStatus = ({ istAdmin = false, hatSonderregel = false, gruppen = [] } = {}) => {
  if (istAdmin) return { art: 'admin', text: 'Admin · immer Vollzugriff' };
  if (hatSonderregel) return { art: 'sonderregel', text: 'Sonderregel aktiv' };
  if (gruppen.length) return { art: 'gruppe', text: `Gruppe: ${gruppen.map((g) => g.name).join(', ')}` };
  return { art: 'standard', text: 'Standardrechte' };
};

/** Alles, was die Seite "Person" über ein Konto wissen muss, aus dem Entwurf. */
export const personSicht = (entwurf, email, istAdmin = false) => {
  const e = normalisiereMail(email);
  const gruppen = groupsForEmail(entwurf.toolGroups || [], e);
  const eigeneRechte = entwurf.toolUser?.[e] || null;
  const eigeneRegel = entwurf.contractUser?.[e] || null;
  const hatSonderregel = Boolean(eigeneRechte || eigeneRegel);
  return {
    email: e,
    istAdmin,
    gruppen,
    hatToolRegel: Boolean(eigeneRechte),
    hatVertragsRegel: Boolean(eigeneRegel),
    hatSonderregel,
    zugriff: wirksamerZugriff({ istAdmin, standard: entwurf.toolDefault, gruppen, persoenlich: eigeneRechte }),
    regel: wirksameVertragsregel({ standard: entwurf.contractDefault, gruppen, persoenlich: eigeneRegel }),
    status: personStatus({ istAdmin, hatSonderregel, gruppen }),
  };
};

/**
 * Ein Recht einer Person ändern. Hat sie noch keine Sonderregel, beginnt die
 * neue bei dem, was sie heute wirklich darf (Standard + aktive Gruppen) - nicht
 * bei "nichts". Eine Sonderregel ersetzt Standard und Gruppen vollständig
 * (mergeAccessMaps); begänne sie leer, verlöre die Person mit dem ersten
 * Häkchen still alle anderen Bereiche, die sie bisher über ihre Gruppe hatte.
 */
export const personenRechtGeaendert = (toolUser, email, geerbt, toolKey, flagKey, checked) => {
  const e = normalisiereMail(email);
  if (!e) return toolUser;
  const ausgang = normalizeAccessMap(toolUser?.[e] || geerbt || createEmptyAccessMap());
  return {
    ...toolUser,
    [e]: normalizeAccessMap({ ...ausgang, [toolKey]: withFlagUpdate(ausgang[toolKey], flagKey, checked) }),
  };
};

/** Vertragslimits einer Person ändern - aus demselben Grund ab der wirksamen Regel. */
export const personenRegelGeaendert = (contractUser, email, geerbt, patch) => {
  const e = normalisiereMail(email);
  if (!e) return contractUser;
  return { ...contractUser, [e]: normalizeContractPolicy({ ...(contractUser?.[e] || geerbt || DEFAULT_CONTRACT_POLICY), ...patch }) };
};

export const ohneEintrag = (karte, email) => {
  const e = normalisiereMail(email);
  return Object.fromEntries(Object.entries(karte || {}).filter(([k]) => k !== e));
};

// ---------- Gruppen (wie updatePermissionGroup & Co. in ToolSettingsPage) ----------

export const gruppeGeaendert = (gruppen, id, patch) => normalizePermissionGroups(
  gruppen.map((g) => (g.id === id ? { ...g, ...patch } : g))
);

export const gruppenRechtGeaendert = (gruppen, id, toolKey, flagKey, checked) => normalizePermissionGroups(
  gruppen.map((g) => {
    if (g.id !== id) return g;
    const vorher = normalizeAccessMap(g.access || createEmptyAccessMap());
    return { ...g, access: normalizeAccessMap({ ...vorher, [toolKey]: withFlagUpdate(vorher[toolKey], flagKey, checked) }) };
  })
);

export const gruppenRegelGeaendert = (gruppen, id, patch) => normalizePermissionGroups(
  gruppen.map((g) => (g.id === id ? { ...g, contractPolicy: normalizeContractPolicy({ ...g.contractPolicy, ...patch }) } : g))
);

export const gruppenSichtGeaendert = (gruppen, id, patch) => normalizePermissionGroups(
  gruppen.map((g) => (g.id === id ? { ...g, dealVisibility: normalizeDealVisibility({ ...g.dealVisibility, ...patch }) } : g))
);

/**
 * Neue Gruppe wie addPermissionGroup - aber mit eindeutiger Kennung. Die
 * Kennung entsteht aus dem Namen; hieße eine neue Gruppe wie eine vorhandene,
 * würde normalizePermissionGroups die vorhandene sonst still ersetzen.
 */
export const neueGruppe = (gruppen, name) => {
  const gruppe = createEmptyGroup(name);
  const vorhanden = new Set(gruppen.map((g) => g.id));
  let id = gruppe.id;
  for (let n = 2; vorhanden.has(id); n += 1) id = `${gruppe.id}-${n}`;
  return { id, gruppen: normalizePermissionGroups([...gruppen, { ...gruppe, id }]) };
};
